<template>
    <div class="mx-auto max-w-screen-md text-black">

        <LoadingWithStatus :status="loadingStatus" v-if="loading"/>

        <div class="mx-auto bg-white rounded-xl p-8 w-full md:w-[550px]" v-if="complete">

            <div class="text-center text-5xl text-brand-primary-500">
                <i class="fa-fw fa-sharp fa-light fa-circle-check mb-4"></i>
            </div>

            <h2 class="text-5xl text-center mb-6">We're On it!</h2>

            <p class="text-center px-4">
                You should have an estimate in your inbox momentarily!
            </p>

            <Button primary @click="handleDevBack" v-if="env === 'local'">
                Back (Dev Only)
            </Button>

        </div>

        <div class="mx-auto" v-else-if="step === 0">

            <h1 class="text-4xl md:text-6xl text-center mb-8 text-white">
                Free Rent Estimate
            </h1>

            <p class="text-lg text-white text-center mb-8">
                In a <b>few short steps</b> you'll know how much your property could rent for
            </p>

            <div class="bg-white p-4 rounded-xl mb-6">
                <div class="flex flex-col md:flex-row gap-4">
                    <div id="search-input" class="flex-1"/>
                </div>
            </div>

            <div class="flex items-center justify-center">
                <div class="text-sm text-white text-center bg-black rounded-full px-3 py-1">
                    <i class="fa-sharp fa-light fa-stopwatch mr-1"></i> Only takes about 60 seconds
                </div>
            </div>

        </div>

        <div v-else-if="step >= 1" class="mx-auto bg-white rounded-xl p-8 w-full md:w-[550px]">

            <h2 class="text-4xl md:text-5xl text-center mb-8">{{ steps[step].title }}</h2>

            <p class="text-center px-3 mb-8" v-if="steps[step].description">
                {{ steps[step].description }}
            </p>

            <div class="mb-12" v-if="step === 1">

                <div class="mb-6">
                    <div class="text-center md:text-xl font-bold">
                        {{ feature.properties.context.address.name }}<br/>
                        {{ feature.properties.context.place.name }}, {{ feature.properties.context.region.region_code }}
                        {{ feature.properties.context.postcode.name }}
                    </div>
                </div>

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-3">
                    <div class="label text-xl">
                        <i class="fa-fw fa-sharp fa-light fa-bed-empty mr-1.5"></i> Bedrooms
                    </div>
                    <input type="number" placeholder="3" min="0" v-model="details.bedrooms"
                           class="rounded p-2 w-full md:w-[160px] text-center" inputmode="numeric"/>
                </div>

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-3">
                    <div class="label text-xl">
                        <i class="fa-fw fa-sharp fa-light fa-bath mr-1.5"></i> Bathrooms
                    </div>
                    <input type="number" placeholder="2" min="0" v-model="details.bathrooms"
                           class="rounded p-2 w-full md:w-[160px] text-center" inputmode="numeric"/>
                </div>

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-3">
                    <div class="label text-xl">
                        <i class="fa-fw fa-sharp fa-light fa-ruler-triangle mr-1.5"></i> SqFt.
                    </div>
                    <input type="number" placeholder="1000" min="0" v-model="details.squareFootage"
                           class="rounded p-2 w-full md:w-[160px] text-center" inputmode="numeric"/>
                </div>

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-3">
                    <div class="label text-xl">
                        <i class="fa-fw fa-sharp fa-light fa-calendar-day mr-1.5"></i> Year Built
                    </div>
                    <input type="number" placeholder="2015" min="1900" v-model="details.year"
                           class="rounded p-2 w-full md:w-[160px] text-center" inputmode="numeric"/>
                </div>

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-6">
                    <div class="label text-xl">
                        <i class="fa-fw fa-sharp fa-light fa-house-building mr-1.5"></i> Type
                    </div>

                    <select v-model="details.propertyType" class="rounded p-2 w-full md:w-[160px]">
                        <option>Single Family</option>
                        <option>Condo</option>
                        <option>Townhouse</option>
                        <option>Manufactured</option>
                        <option>Multi-Family</option>
                        <option>Apartment</option>
                    </select>
                </div>

            </div>

            <div class="mb-12" v-if="step === 2">

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-6">

                    <div class="label text-xl mb-3 md:mb-0">
                        <i class="fa-fw fa-sharp fa-light fa-house-circle-check mr-1.5"></i> Condition
                    </div>

                    <select v-model="details.condition" class="rounded p-2 w-full md:w-auto">
                        <option value="new_like_new">New / Like New</option>
                        <option value="excellent">Excellent</option>
                        <option value="good">Good</option>
                        <option value="fair">Fair</option>
                        <option value="fixer_upper">Fixer-Upper</option>
                    </select>
                </div>

            </div>

            <div class="mb-12" v-if="step === 3">

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-6">

                    <div class="label text-xl mb-3 md:mb-0">
                        <i class="fa-fw fa-sharp fa-light fa-calendar-day mr-1.5"></i> Availability
                    </div>

                    <select v-model="details.availability" class="rounded p-2 w-full md:w-auto">
                        <option>Immediately</option>
                        <option>Within the next 30 Days</option>
                        <option>Within the next 1 to 3 Months</option>
                        <option>Within the next 3 to 6 Months</option>
                        <option>6+ Months</option>
                    </select>
                </div>

            </div>

            <div class="mb-12" v-if="step === 4">

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-6">

                    <div class="label text-xl mb-3 md:mb-0">
                        <i class="fa-fw fa-sharp fa-light fa-envelope mr-1.5"></i> Email *
                    </div>

                    <input type="email"
                           placeholder="name@email.com"
                           v-model="email"
                           class="rounded p-2 w-full md:flex-1"
                           inputmode="email"
                           @keydown.enter="steps[step].next"
                    />

                </div>

                <div
                    class="field flex flex-col md:flex-row items-start md:items-center justify-start md:justify-between gap-3 mb-6">

                    <div class="label text-xl mb-3 md:mb-0">
                        <i class="fa-fw fa-sharp fa-light fa-phone mr-1.5"></i> Phone *
                    </div>

                    <input type="tel"
                           v-model="phone"
                           class="rounded p-2 w-full md:flex-1"
                           placeholder="208-555-1212"
                           pattern="^[0-9\-\(\)\s]+$"
                           inputmode="tel" />

                </div>

            </div>

            <div class="controls flex items-center justify-between">

                <Button outlined @click="handleBack" v-if="step < steps.length - 1">
                    <i class="fa-sharp fa-solid fa-arrow-left"></i>
                </Button>
                <div v-else></div>

                <Button class="bg-[var(--primary-400)] text-white" primary @click="steps[step].next"
                        :class="[step === steps.length - 1 ? 'w-full' : null]">
                    {{ steps[step].cta }} <i class="fa-sharp fa-solid fa-arrow-right" style="margin-left: 5px;"></i>
                </Button>
            </div>

            <div class="w-full bg-gray-200 rounded-full h-2 mt-6" v-if="step < steps.length - 1">
                <div class="bg-brand-primary-500 h-2 rounded-full"
                     :style="{ width: `${step / steps.length * 100}%` }"></div>
            </div>

        </div>
    </div>

</template>

<script>
const delay = ms => new Promise(res => setTimeout(res, ms));

import axios from 'axios';
import {MapboxSearchBox} from '@mapbox/search-js-web';

import Accordion from './Accordion.vue';
import Button from './Button.vue';
import LoadingWithStatus from './LoadingWithStatus.vue';

const searchBoxElement = new MapboxSearchBox();
const loadingStepDelay = window.app.env === 'local' ? 0 : 2000;

const apiUrl = import.meta.env.VITE_API_URL;

export default {
    name: 'ProposalGenerator',
    components: {
        Accordion,
        Button,
        LoadingWithStatus,
    },
    computed: {
        steps() {
            return [
                {
                    title: 'Property Address',
                },
                {
                    title: 'Property Details',
                    description: `Here's what we found for your property. Feel free to adjust, if needed.`,
                    cta: 'Looks Good',
                    next: this.handlePropertyDetailsConfirmed,
                },
                {
                    title: 'Property Condition',
                    description: `How would you describe the condition of your property?`,
                    cta: 'Next',
                    next: this.handlePropertyConditionConfirmed,
                },
                {
                    title: 'Property Availability',
                    description: `How soon would you like to get the property on the market?`,
                    cta: 'Next',
                    next: this.handlePropertyAvailabilityConfirmed,
                },
                {
                    title: 'Last Step',
                    description: `Where should we send your estimate?`,
                    cta: `Get My Estimate`,
                    next: this.handleProposalCreate,
                },
            ];
        },
    },
    mounted() {
        this.searchInput.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;
        this.searchInput.placeholder = 'Enter Your Property Address';

        this.searchInput.options = {
            country: 'US',
            language: 'en',
            types: 'address',
        };

        this.initSearchBox();

        // window.dataLayer.push({
        //   event: 'gtm.estimate.viewedForm',
        // });
    },
    created() {

    },
    data() {
        return {
            complete: false,
            desktopBackgroundImage: null,
            details: {
                id: null,
                availability: 'Immediately',
                bedrooms: null,
                bathrooms: null,
                condition: 'good',
                propertyType: 'Single Family',
                year: null,
                squareFootage: null,
            },
            email: '',
            env: window.app.env || null,
            faqs: [],
            feature: null,
            loading: false,
            loadingStatus: '',
            phone: '',
            searchInput: searchBoxElement,
            step: 0,
        };
    },
    methods: {
        handleBack() {
            if (this.step > 0) {
                this.step -= 1;
                this.complete = false;

                if (this.step === 0) {
                    this.initSearchBox();
                }
            }
        },
        handleDevBack() {
            this.complete = false;
        },
        handleGetStarted() {

            if (this.loading) {
                return;
            }

            if (!this.feature) {

                window.app.snackbar({
                    message: 'You must select a valid address',
                });

                return;
            }

            return this.handleGetPropertyDetails();
        },
        async handleGetPropertyDetails() {

            if (this.loading) {
                return;
            }

            this.loading = true;

            this.loadingStatus = 'Getting Property Details';

            await delay(loadingStepDelay);

            return axios.request({
                url: `${apiUrl}/estimate/property`,
                method: 'POST',
                headers: {
                    'X-Nonce': window.app.nonceEstimate,
                },
                data: {
                    address: this.feature.properties.full_address,
                    postcode: this.feature.properties.context.postcode.name,
                },
            }).then(async (response) => {

                this.loadingStatus = 'Details Received';

                await delay(loadingStepDelay);

                this.loadingStatus = 'Processing';

                await delay(loadingStepDelay);

                const propertyDetails = response.data.propertyDetails;

                if (propertyDetails) {
                    this.details = {
                        ...this.details,
                        ...propertyDetails,
                        id: response.data.propertyId,
                    };
                }

                this.step += 1;

            }).catch(async (error) => {

                this.loadingStatus = 'Details Received';

                await delay(loadingStepDelay);

                window.app.snackbar({
                    message: Object.values(error.response.data.errors).map(key => key.join(' ')).join(' '),
                    duration: 10000,
                    type: 'error',
                });

            }).finally(async () => {
                this.loading = false;
            });
        },
        async handlePropertyAvailabilityConfirmed() {

            this.loading = true;

            this.loadingStatus = 'Saving Progress';

            await delay(loadingStepDelay);

            this.step += 1;

            this.loading = false;

        },
        async handlePropertyConditionConfirmed() {

            this.loading = true;

            this.loadingStatus = 'Saving Progress';

            await delay(loadingStepDelay);

            this.step += 1;

            this.loading = false;

        },
        async handlePropertyDetailsConfirmed() {

            this.loading = true;

            this.loadingStatus = 'Saving Progress';

            await delay(loadingStepDelay);

            this.step += 1;

            this.loading = false;

        },
        async handleProposalCreate() {

            if (this.loading) {
                return false;
            }

            this.loading = true;

            return axios.request({
                url: `${apiUrl}/estimate`,
                method: 'POST',
                headers: {
                    'X-Nonce': window.app.nonceEstimate,
                },
                data: {
                    address: this.feature.properties.full_address,
                    details: {
                        ...this.details,
                    },
                    email: this.email,
                    phone: this.phone,
                },
            }).then(async (response) => {

                this.loadingStatus = 'Submitted Successfully';

                await delay(loadingStepDelay);

                this.complete = true;

                // window.dataLayer.push({
                //   event: 'gtm.estimate.completed',
                // });

            }).catch((error) => {

                window.app.snackbar({
                    message: Object.values(error.response.data.errors).map(key => key.join(' ')).join(' '),
                    duration: 10000,
                    type: 'error',
                });

            }).finally(async () => {

                this.loading = false;

            });

        },
        handlePropertySelected(e) {
            this.feature = e.detail?.features[0];
            this.handleGetStarted();
            // dd(this.feature);
            // window.dataLayer.push({
            //   event: 'gtm.estimate.started',
            // });
        },
        initSearchBox() {
            this.$nextTick(() => {

                const el = document.querySelector('#search-input');

                if (el && this.searchInput) {
                    el.appendChild(this.searchInput);
                    el.querySelector('input').setAttribute('data-1p-ignore', true);
                    this.searchInput.addEventListener('retrieve', this.handlePropertySelected);
                }
            });
        },
    },
}
</script>

<style lang="scss">
input, select {
    border: 1px solid #CCC;
}

#search-input {

    [class$="--SearchBox"] {
        background: transparent;
        box-shadow: none;
    }

    [class$="--SearchIcon"] {
        left: 0.6em;
        height: 25px;
        width: 25px;
    }

    [class$="--ClearBtn"] {
        position: relative;
        right: 0.6em;
        height: 25px;
        width: 25px;
    }

    input[class$="--Input"] {
        border: 0;
        font-family: 'Poppins', sans-serif;
        font-size: 1.2em;
        padding: 15px 45px;
    }
}
</style>
